import { AriaButtonProps, useButton } from '@react-aria/button';
import { useLink } from '@react-aria/link';
import { AriaLinkProps } from '@react-types/link';
import { useTracking } from '@ui/hooks/useTracking';
import { clsx } from 'clsx';
import React, { ComponentType, ElementType, Ref, RefObject, SVGProps, forwardRef } from 'react';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';
import { Link } from 'react-router-dom';

import { TrackingEventProperties } from '../../hooks/trackingEventTaxonomy';
import styles, { ButtonVariants, button } from './button.css';

type MixpanelTrackingProps = {
  trackingProps?: TrackingEventProperties;
};

type CommonButtonProps = {
  // The gap between the icon and the children. Defaults to
  // the padding value. Use `smallToNone` or `largeToNone` if
  // children contains only a visually hidden a11y labels on mobile
  // in order to prevent extra right padding due to grid gap.
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  iconPosition?: 'start' | 'end' | 'mobileOnly';
  to?: string;
  tag?: ElementType;
  'data-testid'?: string;
} & MixpanelTrackingProps;

type BaseButtonOwnProps = CommonButtonProps & ButtonVariants;

type BaseButtonProps<E extends React.ElementType> = PolymorphicComponentProps<E, BaseButtonOwnProps>;
export type ButtonProps<E extends React.ElementType> = PolymorphicComponentProps<E, BaseButtonOwnProps>;

type BoxProps = Parameters<typeof Box>[0];

const defaultMixpanelProps = {
  'location journey': 'Unknown',
  'location page': 'Unknown',
  'location component name': 'Unknown button',
  actor: 'User',
};

export const ButtonBase = forwardRef(
  <E extends React.ElementType>(
    {
      tag = 'button',
      to,
      variant = 'primary',
      padding = 'large',
      gap = 'large',
      icon: Icon,
      iconPosition = 'start',
      iconSize = 'regular',
      children,
      "data-testid": dataTestId,
      trackingProps,
      onClick,
      onPress,
      ...props
    }: BaseButtonProps<E>,
    innerRef: typeof props.ref,
  ) => {
    const { track } = useTracking();

    const handleInteraction = (e: React.MouseEvent | React.KeyboardEvent) => {
      const eventName = to ? 'Link clicked' : 'Button clicked';

      track(eventName, trackingProps ? {
        ...trackingProps,
        actor: trackingProps.actor ?? 'User',
      } : defaultMixpanelProps);

      if (onClick) {
        onClick(e as React.MouseEvent);
      }
      if (onPress) {
        onPress(e);
      }
    };

    const boxProps: BoxProps = {
      as: to ? Link : tag,
      ...(to ? { to } : {}),
      'data-testid': dataTestId,
      ...props,
      onClick: handleInteraction,
      onPress: handleInteraction,
      ref: innerRef,
      className: clsx(
        button({
          padding: variant === 'pagination' ? 'pagination' : padding,
          variant,
          gap: gap ?? padding,
        }),
        props.className,
      ),
    };

    return (
      <Box {...boxProps}>
        {Icon && iconPosition === 'start' && (
          <Icon data-testid="icon-start" aria-hidden="true" className={styles.svg({ size: iconSize })} />
        )}
        {Icon && iconPosition === 'mobileOnly' && <Icon aria-hidden="true" className={styles.mobileSvg} />}
        {children}
        {Icon && iconPosition === 'end' && (
          <Icon data-testid="icon-end" aria-hidden="true" className={styles.svg({ size: iconSize })} />
        )}
      </Box>
    );
  },
) as <E extends React.ElementType>(props: BaseButtonProps<E>) => React.JSX.Element;

// Returns anchor element styled as a button
// Taking in standard anchor link props, in addition to ButtonOwnProps
//
// Example of usage:
// <LinkButton to="https://ocula.tech/" target="_blank" icon={ActionDownloadMediumIcon} padding="large">Download</LinkButton>
export const LinkButton = forwardRef(
  ({ children, variant, padding, gap, ...props }: ButtonProps<'a'>, innerRef: Ref<HTMLAnchorElement>) => {
    const { linkProps } = useLink(props as AriaLinkProps, innerRef as RefObject<HTMLAnchorElement>);

    return (
      <ButtonBase<'a'> {...props} {...linkProps} ref={innerRef} variant={variant} tag="a" padding={padding} gap={gap}>
        {children}
      </ButtonBase>
    );
  },
) as <E extends React.ElementType>(props: ButtonProps<E>) => React.JSX.Element;

// Returns button element styled as a button
// Taking in standard button props, in addition to ButtonOwnProps
//
// Example of usage:
// <Button onPress={() => alert('Button clicked!')} icon={ActionDownloadMediumIcon} padding="large">Action</Button>
export const Button = forwardRef(
  (
    { children, variant, padding, gap, ...props }: Omit<ButtonProps<'button'>, 'type'>,
    innerRef: Ref<HTMLButtonElement>,
  ) => {
    const { buttonProps } = useButton(
      { ...(props as AriaButtonProps), isDisabled: props.disabled },
      innerRef as RefObject<HTMLButtonElement>,
    );

    return (
      <ButtonBase<'button'>
        {...props}
        {...buttonProps}
        ref={innerRef}
        variant={variant}
        padding={padding}
        gap={gap}
      >
        {children}
      </ButtonBase>
    );
  },
) as <E extends React.ElementType>(props: ButtonProps<E>) => React.JSX.Element;
